import { useState, useEffect, useRef } from 'react'
import './BookingModal.css'

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || ''

const INITIAL_FORM = {
  name: '',
  email: '',
  company: '',
  phone: '',
  message: '',
}

export default function BookingModal({ open, onClose }) {
  const [form, setForm]       = useState(INITIAL_FORM)
  const [errors, setErrors]   = useState({})
  const [status, setStatus]   = useState('idle') // idle | submitting | success | error
  const firstInputRef         = useRef(null)
  const modalRef              = useRef(null)

  // Focus first input on open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      setTimeout(() => firstInputRef.current?.focus(), 50)
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    if (open) window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  const validate = () => {
    const errs = {}
    if (!form.name.trim())    errs.name    = 'Your name is required'
    if (!form.email.trim())   errs.email   = 'Email address is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Please enter a valid email'
    if (!form.company.trim()) errs.company = 'Company name is required'
    if (!form.message.trim()) errs.message = 'Please tell us a little about what you\'re hoping to achieve'
    return errs
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(f => ({ ...f, [name]: value }))
    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }

    setStatus('submitting')
    try {
      const res = await fetch(`${API_ENDPOINT}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:    form.name.trim(),
          email:   form.email.trim(),
          company: form.company.trim(),
          phone:   form.phone.trim(),
          message: form.message.trim(),
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setStatus('success')
      setForm(INITIAL_FORM)
    } catch {
      setStatus('error')
    }
  }

  const handleReset = () => {
    setStatus('idle')
    setErrors({})
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!open) return null

  return (
    <div className="modal-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="modal" ref={modalRef}>
        <button className="modal__close" onClick={onClose} aria-label="Close modal">
          <CloseIcon />
        </button>

        {status === 'success' ? (
          <SuccessState onClose={onClose} />
        ) : status === 'error' ? (
          <ErrorState onRetry={handleReset} onClose={onClose} />
        ) : (
          <>
            <div className="modal__header">
              <h2 className="modal__title" id="modal-title">Book a Free Discovery Call</h2>
              <p className="modal__subtitle">
                30 minutes. No pitch. An honest conversation about where AI can deliver real value for your business.
              </p>
            </div>

            <form className="modal__form" onSubmit={handleSubmit} noValidate>
              <div className="form__row form__row--two">
                <Field label="Your name" name="name" type="text"
                       value={form.name} onChange={handleChange} error={errors.name}
                       placeholder="Jane Smith" required inputRef={firstInputRef} />
                <Field label="Email address" name="email" type="email"
                       value={form.email} onChange={handleChange} error={errors.email}
                       placeholder="jane@company.com" required />
              </div>

              <div className="form__row form__row--two">
                <Field label="Company" name="company" type="text"
                       value={form.company} onChange={handleChange} error={errors.company}
                       placeholder="Acme Ltd" required />
                <Field label="Phone" name="phone" type="tel"
                       value={form.phone} onChange={handleChange} error={errors.phone}
                       placeholder="+44 7000 000000" hint="Optional" />
              </div>

              <div className="form__row">
                <label className="form__label" htmlFor="message">
                  What are you hoping to achieve?
                  <span className="form__required" aria-hidden="true"> *</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  className={`form__textarea${errors.message ? ' form__textarea--error' : ''}`}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="e.g. We process a high volume of supplier invoices manually — we're interested in whether an AI agent could handle the extraction and routing..."
                  rows={4}
                  required
                />
                {errors.message && <span className="form__error" role="alert">{errors.message}</span>}
              </div>

              <button type="submit" className="btn btn--primary btn--primary-lg modal__submit" disabled={status === 'submitting'}>
                {status === 'submitting' ? (
                  <><Spinner /> Submitting…</>
                ) : (
                  <>Book My Discovery Call <ArrowIcon /></>
                )}
              </button>

              <p className="modal__privacy">
                <LockIcon /> Your details are stored securely and never shared with third parties.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

function Field({ label, name, type, value, onChange, error, placeholder, hint, required, inputRef }) {
  return (
    <div className="form__field">
      <label className="form__label" htmlFor={name}>
        {label}
        {required && <span className="form__required" aria-hidden="true"> *</span>}
        {hint && <span className="form__hint"> — {hint}</span>}
      </label>
      <input
        id={name}
        ref={inputRef}
        name={name}
        type={type}
        className={`form__input${error ? ' form__input--error' : ''}`}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        autoComplete={type === 'email' ? 'email' : type === 'tel' ? 'tel' : 'off'}
      />
      {error && <span className="form__error" role="alert">{error}</span>}
    </div>
  )
}

function SuccessState({ onClose }) {
  return (
    <div className="modal__state">
      <div className="modal__state-icon modal__state-icon--success">
        <CheckIcon />
      </div>
      <h2 className="modal__state-title">Request received</h2>
      <p className="modal__state-body">
        Thanks for getting in touch. We&rsquo;ll review your message and be in touch within one
        business day to confirm your discovery call.
      </p>
      <button className="btn btn--primary btn--primary-lg" onClick={onClose}>
        Back to site
      </button>
    </div>
  )
}

function ErrorState({ onRetry, onClose }) {
  return (
    <div className="modal__state">
      <div className="modal__state-icon modal__state-icon--error">
        <AlertIcon />
      </div>
      <h2 className="modal__state-title">Something went wrong</h2>
      <p className="modal__state-body">
        We weren&rsquo;t able to submit your request. Please try again, or email us directly at{' '}
        <a href="mailto:hello@northcortex.co.uk">hello@northcortex.co.uk</a>.
      </p>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        <button className="btn btn--primary btn--primary-lg" onClick={onRetry}>Try again</button>
        <button className="btn btn--outline-white" onClick={onClose} style={{ borderColor: 'var(--grey-border)', color: 'var(--text-secondary)' }}>Close</button>
      </div>
    </div>
  )
}

/* ── Icons ── */
function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  )
}
function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}
function CheckIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  )
}
function AlertIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  )
}
function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  )
}
function Spinner() {
  return <span className="spinner" aria-hidden="true" />
}
